import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import { useHistory, useParams } from 'react-router-dom';
import {
  Container,
  Row,
  Card,
  Form,
  CardHeader,
  CardBody,
  Col
} from 'reactstrap';
import ReactLoader from 'components/Loader';
import ReactButton from 'components/Buttons/ReactButton';
import { ScreenPrompt } from 'components/ScreenPrompt';
import { FlexControllerV2 } from 'components/FlexControllerInput/V2';

import useQueues from 'hooks/useQueues';
import useMonitoringQueueGroups from 'hooks/useMonitoringQueueGroups';
import UserService from 'views/User/service';

const MonitoringQueueGroupForm = () => {
  const history = useHistory();
  const { id } = useParams();

  const [loadingGroup, setLoadingGroup] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [usersOptions, setUsersOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const { options: queueOptions, isLoading: loadingQueues } = useQueues({
    showDisabled: false,
    types: ['is_chat', 'is_telephony']
  });

  const { getGroupById, createGroup, updateGroup } = useMonitoringQueueGroups();

  useEffect(() => {
    setLoadingUsers(true);
    UserService.getAllSeachUser(1, 1000, '', false, '', 'name', 'ASC')
      .then((response) => {
        const users = response.data.data.map((u) => ({
          label: u.name || u.login,
          value: u.login
        }));
        setUsersOptions(users);
      })
      .catch((err) => {
        console.error('Erro ao carregar usuários', err);
      })
      .finally(() => {
        setLoadingUsers(false);
      });
  }, []);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting }
  } = useForm({
    defaultValues: {
      name: '',
      queues: [],
      agents: [],
      accessAgentsLogin: []
    }
  });

  useEffect(() => {
    if (id) {
      setLoadingGroup(true);
      setIsUpdate(true);

      getGroupById(id)
        .then((groupData) => {
          if (groupData) {
            const formattedQueues = groupData.queues
              ? groupData.queues.map((q) => ({
                  label: q.name || q.queue_name || `Fila ${q.id || q}`,
                  value: q.id || q
                }))
              : [];

            const formattedAgents = groupData.agents
              ? groupData.agents.map((a) => ({
                  label: a.name || a.login,
                  value: a.login
                }))
              : [];

            const formattedAccessAgents = groupData.accessAgentsLogin
              ? groupData.accessAgentsLogin.map((a) => ({
                  label: a.name || a.login,
                  value: a.login
                }))
              : [];

            reset({
              name: groupData.name,
              queues: formattedQueues,
              agents: formattedAgents,
              accessAgentsLogin: formattedAccessAgents
            });
          } else {
            history.push('/admin/chat/monitoring-queue-group');
          }
        })
        .finally(() => {
          setLoadingGroup(false);
        });
    }
  }, [id, reset, history, getGroupById]);

  const onSubmit = useCallback(
    async (data) => {
      setLoadingGroup(true);

      const payload = {
        name: data.name,
        queues: data.queues ? data.queues.map((q) => q.value) : [],
        agents: data.agents ? data.agents.map((a) => a.value) : [],
        accessAgentsLogin: data.accessAgentsLogin
          ? data.accessAgentsLogin.map((a) => a.value)
          : []
      };

      let success = false;
      if (isUpdate) {
        success = await updateGroup(id, payload);
      } else {
        success = await createGroup(payload);
      }

      if (success) {
        history.push('/admin/chat/monitoring-queue-group');
      }
      setLoadingGroup(false);
    },
    [history, isUpdate, id, createGroup, updateGroup]
  );

  const cancelAction = () => {
    history.push('/admin/chat/monitoring-queue-group');
  };

  const isLoading = loadingGroup || loadingQueues || loadingUsers;

  return (
    <>
      <SimpleHeader
        name={isUpdate ? 'Editar Agrupamento' : 'Novo Agrupamento'}
        parentName="Monitoramento"
        returnPath="/admin/chat/monitoring-queue-group"
        buttonTitle="Voltar"
      />

      <ScreenPrompt
        when={isDirty && !isSubmitting}
        message="Você tem alterações não salvas. Tem certeza que deseja sair?"
      />

      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">Dados do Agrupamento</h3>
                </CardHeader>
                <CardBody>
                  {isLoading ? (
                    <Row className="justify-content-md-center py-5">
                      <ReactLoader />
                    </Row>
                  ) : (
                    <Form onSubmit={handleSubmit(onSubmit)}>
                      <Row>
                        <Col md="6">
                          <FlexControllerV2
                            type="text"
                            name="name"
                            control={control}
                            label="Nome do Agrupamento"
                            placeholder="Ex: Suporte Nível 1"
                            rules={{
                              required: 'O nome é obrigatório',
                              minLength: {
                                value: 3,
                                message:
                                  'O nome deve ter no mínimo 3 caracteres'
                              }
                            }}
                            errors={errors}
                          />
                        </Col>

                        <Col md="6">
                          <FlexControllerV2
                            type="select"
                            name="queues"
                            control={control}
                            label="Filas"
                            placeholder="Selecione as filas..."
                            options={queueOptions}
                            isMulti
                            rules={{
                              validate: (value) =>
                                (value && value.length > 0) ||
                                'Selecione pelo menos uma fila'
                            }}
                            errors={errors}
                          />
                        </Col>
                        <Col md="6" className="mt-2">
                          <FlexControllerV2
                            type="select"
                            name="agents"
                            control={control}
                            label="Agentes"
                            placeholder="Selecione os agentes (vazio = todos)..."
                            options={usersOptions}
                            isMulti
                            errors={errors}
                          />
                        </Col>
                        <Col md="6" className="mt-2">
                          <FlexControllerV2
                            type="select"
                            name="accessAgentsLogin"
                            control={control}
                            label="Permissão de Visualização (Usuários)"
                            placeholder="Selecione os usuários..."
                            options={usersOptions}
                            isMulti
                            rules={{
                              validate: (value) =>
                                (value && value.length > 0) ||
                                'Selecione pelo menos um usuário'
                            }}
                            errors={errors}
                          />
                        </Col>
                      </Row>

                      <hr />

                      <div className="d-flex">
                        <ReactButton btnColor="confirmation" type="submit">
                          Salvar
                        </ReactButton>
                        <ReactButton
                          btnColor="cancelation"
                          onClick={cancelAction}
                          type="button"
                          className="ml-2"
                        >
                          Cancelar
                        </ReactButton>
                      </div>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default MonitoringQueueGroupForm;
